import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, CircularProgress, Alert, Grid } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getUserFeedbackStats } from '../../api/user';

const COLORS = ['#43a047', '#e53935']; // Green for correct, Red for incorrect

const FeedbackStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const response = await getUserFeedbackStats();
                setStats(response.data);
            } catch (err) {
                setError('Failed to fetch feedback statistics.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!stats || stats.totalFeedback === 0) {
        return (
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">No Feedback Data</Typography>
                <Typography>You haven't provided any feedback yet.</Typography>
            </Paper>
        );
    }

    const data = [
        { name: 'Correct Feedback', value: stats.correctFeedback },
        { name: 'Incorrect Feedback', value: stats.incorrectFeedback },
    ];

    return (
        <Paper elevation={4} sx={{ p: 4, borderRadius: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight={700} color="primary">
                Feedback Contribution
            </Typography>
            <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={120}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6">Total Feedback Submitted:</Typography>
                    <Typography variant="h3" color="primary">{stats.totalFeedback}</Typography>
                    <Typography variant="h6" sx={{ mt: 2 }}>Correct:</Typography>
                    <Typography variant="h4" sx={{ color: COLORS[0] }}>{stats.correctFeedback}</Typography>
                    <Typography variant="h6" sx={{ mt: 2 }}>Incorrect:</Typography>
                    <Typography variant="h4" sx={{ color: COLORS[1] }}>{stats.incorrectFeedback}</Typography>
                    <Typography variant="h6" sx={{ mt: 2 }}>Accuracy Rate:</Typography>
                    <Typography variant="h4" color="secondary">{stats.accuracyRate.toFixed(2)}%</Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default FeedbackStats;